import { useAuthStore } from '@/stores/AuthStore';
import { IS_NATIVE, postToNative } from '@/lib/nativeBridge';
const API_URL = import.meta.env.VITE_API_URL ?? '';

type BackendLoginResponse = {
  success: boolean;
  data?: {
    userId?: string;
  };
};

export type BackendSessionVerification =
  | { kind: 'authenticated'; userId: string }
  | { kind: 'unauthorized' }
  | { kind: 'failure'; status?: number; retryable: boolean };

export function getGhComponent(): GhComponentApi | null {
  return window.ghComponent ?? null;
}

export function isLocalhostRuntime(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1' || host === '::1';
}

export function showGhLogin(): boolean {
  if (isLocalhostRuntime()) {
    void promptLocalJwtAndSync();
    return true;
  }
  if (IS_NATIVE) {
    postToNative('auth.requestLogin');
    return true;
  }
  const gh = getGhComponent();
  if (!gh?.showLogin) {
    return false;
  }
  gh.showLogin();
  return true;
}

export function getAccessTokenFromGhState(authState?: GhAuthState): string | null {
  if (typeof authState?.ghTokens === 'string') {
    const legacyToken = authState.ghTokens.trim();
    if (legacyToken) return legacyToken;
  }

  const ghTokens = typeof authState?.ghTokens === 'object' ? authState.ghTokens : null;
  const registeredToken = ghTokens?.registered?.access_token?.trim();
  if (registeredToken) return registeredToken;

  const payloadToken = authState?.tokens?.access_token?.trim();
  if (payloadToken) return payloadToken;

  return null;
}

export function getAccessTokenFromGhCookie(ghComponent: GhComponentApi): string | null {
  try {
    const token = ghComponent.getLoggedCookie?.()?.access_token?.trim();
    return token || null;
  } catch {
    return null;
  }
}

export function hasGhRefreshCredential(ghComponent: GhComponentApi): boolean {
  try {
    const refreshCredential = ghComponent.getRefreshLoggedCookie?.();
    return typeof refreshCredential === 'string' && refreshCredential.trim().length > 0;
  } catch {
    return false;
  }
}

export async function promptLocalJwtAndSync(): Promise<boolean> {
  if (!isLocalhostRuntime()) return false;

  const existingToken = useAuthStore.getState().token?.trim();
  if (existingToken) {
    if (isGhTokenExpired(existingToken)) {
      useAuthStore.getState().clearAuth();
    } else {
      return ensureBackendLoginSync(true);
    }
  }

  const tokenInput = window.prompt('Enter JWT token for local login');
  const token = tokenInput?.trim();
  if (!token) return false;

  if (isGhTokenExpired(token)) {
    useAuthStore.getState().clearAuth();
    return false;
  }

  useAuthStore.getState().setGhAuth({
    token,
    status: 'authenticated',
  });

  return ensureBackendLoginSync(true);
}

export async function ensureLocalhostJwtSession(): Promise<boolean> {
  if (!isLocalhostRuntime()) return false;

  const existingToken = useAuthStore.getState().token?.trim();
  if (!existingToken) {
    return promptLocalJwtAndSync();
  }

  if (isGhTokenExpired(existingToken)) {
    useAuthStore.getState().clearAuth();
    return promptLocalJwtAndSync();
  }

  const synced = await ensureBackendLoginSync(true);
  if (synced) {
    return true;
  }

  useAuthStore.getState().clearAuth();
  return promptLocalJwtAndSync();
}

let ghRefreshRequestPromise: Promise<GhRefreshResponse | null> | null = null;
const backendVerificationPromises = new Map<string, Promise<BackendSessionVerification>>();
const TOKEN_EXPIRY_SKEW_SECONDS = 30;

function parseJwtPayload(token: string): { exp?: number } | null {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const json = atob(padded);
    return JSON.parse(json) as { exp?: number };
  } catch {
    return null;
  }
}

export function isGhTokenExpired(
  token: string | null,
  skewSeconds = TOKEN_EXPIRY_SKEW_SECONDS
): boolean {
  if (!token) return true;
  const payload = parseJwtPayload(token);
  if (!payload?.exp) return true;
  const now = Math.floor(Date.now() / 1000);
  return payload.exp <= now + skewSeconds;
}

export function verifyBackendSessionToken(
  token: string,
  signal?: AbortSignal
): Promise<BackendSessionVerification> {
  const normalizedToken = token.trim();
  if (!normalizedToken) {
    return Promise.resolve({ kind: 'failure', retryable: false });
  }

  const existingRequest = backendVerificationPromises.get(normalizedToken);
  if (existingRequest) return existingRequest;

  const request = (async (): Promise<BackendSessionVerification> => {
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: normalizedToken }),
        signal,
      });

      if (response.status === 401) {
        return { kind: 'unauthorized' };
      }

      if (!response.ok) {
        return {
          kind: 'failure',
          status: response.status,
          retryable: response.status >= 500 || response.status === 408 || response.status === 429,
        };
      }

      const body = (await response.json()) as BackendLoginResponse;
      const userId = body?.data?.userId?.trim();
      if (!userId) {
        return { kind: 'failure', status: response.status, retryable: false };
      }

      return { kind: 'authenticated', userId };
    } catch {
      return { kind: 'failure', retryable: true };
    }
  })();

  backendVerificationPromises.set(normalizedToken, request);
  void request.finally(() => {
    if (backendVerificationPromises.get(normalizedToken) === request) {
      backendVerificationPromises.delete(normalizedToken);
    }
  });

  return request;
}

export async function requestGhAccessTokenRefresh(
  ghComponent: GhComponentApi,
  rejectedToken?: string | null
): Promise<string | null> {
  if (!ghComponent.refreshUserToken) return null;

  if (!ghRefreshRequestPromise) {
    const request = Promise.resolve()
      .then(() => ghComponent.refreshUserToken?.() ?? null)
      .catch(() => null);
    ghRefreshRequestPromise = request;
    void request.finally(() => {
      if (ghRefreshRequestPromise === request) ghRefreshRequestPromise = null;
    });
  }

  const result = await ghRefreshRequestPromise;
  const responseToken = result?.data?.token?.access_token?.trim();
  const flatResponseToken = result?.data?.access_token?.trim();
  const cookieToken = getAccessTokenFromGhCookie(ghComponent);
  const refreshedToken = responseToken || flatResponseToken || cookieToken;

  if (!refreshedToken) return null;
  if (rejectedToken?.trim() === refreshedToken) return null;
  return refreshedToken;
}

export async function syncProfile(): Promise<boolean> {
  const token = useAuthStore.getState().token;
  if (!token) return false;

  try {
    const res = await fetch(`${API_URL}/api/auth/sync-profile`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });

    if (!res.ok) return false;

    const body = (await res.json()) as BackendLoginResponse;
    const userId = body?.data?.userId ?? null;
    if (!userId) return false;

    useAuthStore.getState().setLoginSyncedUser({ userId });
    return true;
  } catch {
    return false;
  }
}

export async function ensureBackendLoginSync(force = false): Promise<boolean> {
  const authState = useAuthStore.getState();
  const token = authState.token?.trim();
  if (!token) return false;

  if (!force && authState.isLoginSynced && !!authState.userId) {
    return true;
  }

  const result = await verifyBackendSessionToken(token);
  if (useAuthStore.getState().token?.trim() !== token) {
    return false;
  }

  if (result.kind === 'authenticated') {
    useAuthStore.getState().resolveAuthenticatedSession({ token, userId: result.userId });
    return true;
  }

  useAuthStore.getState().markLoginUnsynced();
  return false;
}

export async function refreshGhAccessToken(options?: {
  interactive?: boolean;
}): Promise<string | null> {
  const interactive = options?.interactive ?? false;
  const currentToken = useAuthStore.getState().token?.trim();
  if (!currentToken && !IS_NATIVE) return null;

  if (IS_NATIVE) {
    // Native is the token authority — ask it to re-authenticate. The provider's
    // persistent token listener commits the session when native responds.
    postToNative('auth.requestLogin');
    return null;
  }

  const ghComponent = getGhComponent();
  const settleTerminalFailure = () => {
    const latestSession = useAuthStore.getState();
    if (latestSession.token?.trim() !== currentToken) return null;

    latestSession.resolveAnonymousSession();
    if (interactive) {
      showGhLogin();
    }
    return null;
  };

  if (!ghComponent?.refreshUserToken) return settleTerminalFailure();

  const refreshedToken = await requestGhAccessTokenRefresh(ghComponent, currentToken);
  if (!refreshedToken) {
    return hasGhRefreshCredential(ghComponent) ? null : settleTerminalFailure();
  }

  const verification = await verifyBackendSessionToken(refreshedToken);
  if (verification.kind === 'unauthorized') return settleTerminalFailure();
  if (verification.kind === 'failure') return null;

  const latestSession = useAuthStore.getState();
  if (
    latestSession.sessionState === 'authenticated' &&
    latestSession.token?.trim() === refreshedToken
  ) {
    return refreshedToken;
  }
  if (latestSession.token?.trim() !== currentToken) {
    return null;
  }

  useAuthStore.getState().resolveAuthenticatedSession({
    token: refreshedToken,
    userId: verification.userId,
  });
  return refreshedToken;
}

export async function ensureFreshGhAccessToken(): Promise<string | null> {
  const token = useAuthStore.getState().token;
  if (!token) {
    return null;
  }

  if (isLocalhostRuntime()) {
    // Local JWT debug flow has no GH refresh provider; keep the manual token as-is.
    return token;
  }
  if (IS_NATIVE) {
    // Native proactively delivers fresh tokens via auth.tokenDelivery.
    // Skip the expiry check here — attempting a GH refresh would fail anyway.
    return token;
  }
  if (!isGhTokenExpired(token)) {
    return token;
  }
  return refreshGhAccessToken({ interactive: false });
}
